import {
  composeRefs,
  isComponentOfType,
  refContainsTarget,
  useClickedOutside,
  useStableCallback,
} from "@prismicio/editor-support/React"
import {
  PopoverAnchor,
  PopoverContent,
  PopoverPortal,
  Root,
} from "@radix-ui/react-popover"
import { clsx } from "clsx"
import {
  Children,
  createContext,
  forwardRef,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
} from "react"
import { RemoveScroll } from "react-remove-scroll"

import type { SX } from "../../theme"
import { AnimatedElement } from "../AnimatedElement"
import { FormSearchInput } from "../Form"
import type { RoundedSide } from "../Form/FormField"
import { Icon, type IconProps } from "../Icon"
import { SearchInput } from "../SearchInput"
import styles from "./ComboBox.module.css"
import { PICK_EVENT } from "./ComboBoxConstants"
import {
  getSelectedItem,
  useInternalSelection,
  useScrollIntoView,
  useSelectFirst,
} from "./useComboBox"

type Variant = "attached" | "detached" | "detached-connected"
interface Context {
  /**
   * Internal state controlling whether the "detached" Content is displayed or not
   */
  listIsOpen: boolean
  /**
   * When attached, listIsOpen is locked to true
   */
  attached: boolean
  /**
   * Sets internal state controlling whether the "detached" Content is displayed or not
   */
  setListIsOpen: (value: boolean) => void
  contentInnerRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
  detachedInputContainerRef: RefObject<HTMLDivElement>
  inputInnerRef: RefObject<HTMLInputElement>
  /**
   * Currently internally "selected" item value
   */
  selectedValue: string | undefined
  /**
   * Sets internally selected item state
   */
  onChangeInternalSelection: (value: string | undefined) => void
  /**
   * Dispatches a new PICK event
   * Runs the onCheckedChange of a ComboBoxItem when it is externally "selected" or picked
   */
  onItemPicked: () => void
  /**
   * Variant defines the appearance and behavior of the ComboBox
   * - "detached" - the dropdown is hiding or appearing according to user focus on the input
   * - "detached-connected" - same as "detached" but the dropdown is connected to the input visually
   * - "attached" - dropdown is connected to the input both visually and functionally (it's always shown)
   */
  variant: Variant
  /**
   * Aligns content above the input or below
   * Supports only "detached*" variants
   */
  side: "top" | "bottom"
  /**
   * Id of ComboBoxContent element
   * Used for aria-controls
   */
  contentId: string
  forceNoFormInput: boolean
}

const ComboBoxContext = createContext<Context | null>(null)
function useComboBox() {
  const context = useContext(ComboBoxContext)
  if (!context) {
    throw new Error("useComboBox must be used inside a ComboboxProvider")
  }

  return context
}

export interface ComboBoxProps {
  children: ReactNode
  /**
   * Variant defines the appearance and behavior of the ComboBox
   * - "detached" - the dropdown is hiding or appearing according to user focus on the input
   * - "detached-connected" - same as "detached" but the dropdown is connected to the input visually
   * - "attached" - dropdown is connected to the input both visually and functionally (it's always shown)
   */
  variant?: Variant
  /**
   * Aligns content above the input or below
   * Supports only "detached*" variants
   */
  side?: "top" | "bottom"
  sx?: SX
  /**
   * Controls the visibility of the list
   * Enables "detached*" variants to be controlled from the outside
   */
  isOpen?: boolean
  /**
   * Callback to be called when the list is closed
   * Enables "detached*" variants to be controlled from the outside
   */
  onClose?: () => void
  /**
   * Refs to elements that should not close the list when clicked
   */
  dropdownRefs?: RefObject<HTMLDivElement>[]
  /**
   * Enables a translucent grey overlay that appears when the list is open
   * Supports only "detached*" variants
   */
  overlay?: boolean
  /**
   * Force no FormInput, to not display input borders twice.
   * Useful when you want to add FormInputField over ComboBox manually
   * ie. to display an error message or description.
   * In the future, we should create FormComboBox component that wraps ComboBox into FormInputField.
   * Tech debt ticket: https://linear.app/prismic/issue/PBD-1867/editor-ui-refactor-combobox-and-select-components
   */
  forceNoFormInput?: boolean
}

export const ComboBox = forwardRef<HTMLDivElement, ComboBoxProps>(
  function ComboBox(props, ref) {
    const {
      children,
      variant = "detached",
      sx,
      isOpen,
      onClose,
      dropdownRefs = [],
      side = "bottom",
      overlay = false,
      forceNoFormInput = false,
    } = props

    const contentRef = useRef<HTMLDivElement>(null)
    const contentInnerRef = useRef<HTMLDivElement>(null)
    const inputInnerRef = useRef<HTMLInputElement>(null)
    const detachedInputContainerRef = useRef<HTMLDivElement>(null)
    const comboBoxInnerRef = useRef<HTMLDivElement>(null)

    const {
      next,
      prev,
      selectedValue,
      listIsOpen: listIsOpenLocal,
      setListIsOpen,
      onChangeInternalSelection,
      onItemPicked,
      cleanupInternalState,
    } = useInternalSelection({
      contentInnerRef,
      inputInnerRef,
    })

    useClickedOutside([comboBoxInnerRef, contentRef, ...dropdownRefs], () => {
      cleanupInternalState()
      isOpen && onClose?.()
    })

    useScrollIntoView({ contentInnerRef })

    const attached = variant === "attached"
    const listIsOpen = isOpen ?? (attached || listIsOpenLocal)

    useSelectFirst({
      contentInnerRef,
      listIsOpen,
      onChangeInternalSelection,
    })

    const isOverlayVisible = overlay && listIsOpen && !attached

    const contentId = useId()

    return (
      <>
        {overlay && (
          <AnimatedElement>
            {isOverlayVisible && <div className={clsx(styles.overlay)} />}
          </AnimatedElement>
        )}
        <div
          ref={composeRefs(comboBoxInnerRef, ref)}
          role="combobox"
          aria-expanded={listIsOpen}
          aria-controls={contentId}
          tabIndex={-1}
          style={sx}
          className={clsx(styles.combobox, attached && styles.attached)}
          onKeyDown={(event) => {
            if (!event.defaultPrevented) {
              switch (event.key) {
                case "ArrowDown": {
                  next(event)
                  break
                }
                case "ArrowUp": {
                  prev(event)
                  break
                }
                case "Enter": {
                  onItemPicked()
                  cleanupInternalState()
                  break
                }
                case "Escape": {
                  cleanupInternalState()
                  onClose?.()
                  break
                }
                case "Tab": {
                  cleanupInternalState()
                }
              }
            }
          }}
        >
          <RemoveScroll enabled={isOverlayVisible}>
            <ComboBoxContext.Provider
              value={{
                inputInnerRef,
                contentRef,
                contentInnerRef,
                selectedValue,
                setListIsOpen,
                listIsOpen,
                onChangeInternalSelection,
                onItemPicked,
                attached,
                detachedInputContainerRef,
                variant,
                side,
                contentId,
                forceNoFormInput,
              }}
            >
              {children}
            </ComboBoxContext.Provider>
          </RemoveScroll>
        </div>
      </>
    )
  },
)

interface ComboBoxContentProps {
  children: ReactNode
  /**
   * Adds padding to the top and bottom of the content
   */
  paddingBlock?: 0 | 2 | 4
  /**
   * Maximum height of the content
   */
  maxHeight?: 200 | "full"
  /**
   * Disable default onClick handler.
   * Useful when you want to handle click events manually,
   * ie. with a Link component to make cmd+click work.
   */
  disableOnClick?: boolean
}

export const ComboBoxContent = forwardRef<HTMLDivElement, ComboBoxContentProps>(
  function ComboBoxContent(
    { children, paddingBlock = 0, maxHeight = 200, disableOnClick },
    ref,
  ) {
    const {
      contentRef,
      contentInnerRef,
      listIsOpen,
      onItemPicked,
      attached,
      detachedInputContainerRef,
      variant,
      side,
      contentId,
      forceNoFormInput,
    } = useComboBox()

    const childNodes = Children.toArray(children)

    const actions = childNodes.filter((child) =>
      isComponentOfType(child, ComboboxAction),
    )

    const rest = childNodes.filter(
      (child) => !isComponentOfType(child, ComboboxAction),
    )

    const content = (
      <div
        ref={contentRef}
        className={clsx(
          styles.comboboxContent,
          attached && styles.attached,
          styles[`side-${side}`],
          variant === "detached-connected" && styles["detached-connected"],
          forceNoFormInput && styles["no-form-input"],
        )}
      >
        <div
          id={contentId}
          ref={composeRefs(ref, contentInnerRef)}
          role="listbox"
          tabIndex={-1}
          className={clsx(
            styles.items,
            paddingBlock && styles[`paddingBlock-${paddingBlock}`],
            maxHeight && styles[`maxHeight-${maxHeight}`],
          )}
          onKeyDown={(event) => {
            // Stop event propagation so in a nested ComboBox events don't get handled by the parent
            event.stopPropagation()
          }}
          onWheel={(event) => {
            // Fix scrolling ComboBoxContent inside of a Dialog
            // https://github.com/radix-ui/primitives/issues/1159
            event.stopPropagation()
          }}
          onClick={(event) => {
            if (disableOnClick) return
            event.preventDefault()
            if (refContainsTarget(contentInnerRef, event.currentTarget)) {
              onItemPicked()
            }
          }}
        >
          {rest}
        </div>
        {actions}
      </div>
    )

    // Locks to true in attached variant
    if (listIsOpen === false) return null

    if (attached === false)
      return (
        <Root open={listIsOpen}>
          <PopoverAnchor virtualRef={detachedInputContainerRef} />
          <PopoverPortal>
            <PopoverContent
              asChild
              align="start"
              side={side}
              avoidCollisions={false}
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              {content}
            </PopoverContent>
          </PopoverPortal>
        </Root>
      )

    return content
  },
)

type ComboBoxItemProps = {
  value: string
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  children: ReactNode
  iconStyle?: Pick<IconProps, "color" | "size">
  /**
   * Changes the style of the item
   * "action-list-item" styles the item as if it was an ActionListItem component
   */
  variant?: "default" | "action-list-item"
}

export const ComboBoxItem = forwardRef<HTMLDivElement, ComboBoxItemProps>(
  function ComboBoxItem(props, forwardRef) {
    const {
      value,
      checked,
      onCheckedChange,
      disabled = false,
      variant = "default",
      iconStyle = { size: "medium" },
    } = props
    const ref = useRef<HTMLDivElement>(null)

    const { selectedValue, onChangeInternalSelection, setListIsOpen } =
      useComboBox()

    const onSelectEvent = useStableCallback(() => {
      onCheckedChange?.(!checked)
      setListIsOpen(false)
    })

    useEffect(() => {
      const element = ref.current
      if (!element || disabled) return
      element.addEventListener(PICK_EVENT, onSelectEvent)
      return () => element.removeEventListener(PICK_EVENT, onSelectEvent)
    }, [disabled, onSelectEvent])

    return (
      <div
        ref={composeRefs(ref, forwardRef)}
        role="option"
        className={clsx(
          styles.comboboxItem,
          variant === "action-list-item" && styles["action-list-item"],
        )}
        combobox-item=""
        tabIndex={-1}
        aria-selected={selectedValue === value}
        aria-disabled={disabled}
        aria-checked={checked}
        data-value={value}
        // Why on mouse move not on mouse enter?
        // Since the list can be scrolled both with the keyboard and the mouse,
        // scrolling with the keyboard triggers onMouseEnter if the mouse sits
        // anywhere within the list.
        onMouseMove={() => onChangeInternalSelection(value)}
      >
        {props.children}
        {checked && (
          <Icon
            name="check"
            {...iconStyle}
            sx={{
              flexShrink: 0,
              marginLeft: "auto",
              marginRight: 8,
              marginBlock: 4,
            }}
          />
        )}
      </div>
    )
  },
)

interface ComboBoxInputProps {
  value: string
  onValueChange: (search: string) => void
  id?: string
  placeholder?: string
  /**
   * Auto focuses the input when variant is "attached".
   * Needed when ComboBox is nested within Popovers
   */
  autoFocus?: boolean
  /**
   * Callback to be called when the input is focused
   * Returning false will prevent the list from opening
   */
  onFocus?: () => boolean
  maxLength?: number

  startAdornment?: boolean
  endAdornment?: boolean
}

export const ComboBoxInput = forwardRef<HTMLInputElement, ComboBoxInputProps>(
  function ComboBoxInput(props, forwardedRef) {
    const {
      id,
      onValueChange,
      value,
      placeholder,
      autoFocus = false,
      maxLength,
      endAdornment = false,
      startAdornment = true,
    } = props
    const {
      contentInnerRef,
      setListIsOpen,
      inputInnerRef,
      listIsOpen,
      attached,
      detachedInputContainerRef,
      variant,
      side,
      forceNoFormInput,
    } = useComboBox()

    function onFocus() {
      const shouldOpen = props.onFocus?.() ?? true
      setListIsOpen(shouldOpen)
    }

    const inputProps = {
      id,
      ref: composeRefs(forwardedRef, inputInnerRef),
      placeholder: placeholder,
      role: "combobox",
      "aria-expanded": listIsOpen,
      "aria-activedescendant": getSelectedItem(contentInnerRef),
      value: value ?? "",
      onValueChange: (value: string) => onValueChange?.(value),
      onFocus: onFocus,
      startAdornment,
      endAdornment,
      roundedSide:
        variant === "detached-connected" && listIsOpen
          ? sideToRoundedSide(side)
          : "all",
      maxLength,
    }

    useLayoutEffect(() => {
      if (attached && autoFocus) {
        // autofocus is unreliable in a nested ComboBox inside a Dialog (Search Filters use case)
        // due to ref availability issues across browsers. This solution schedules the autofocus before
        // the next repaint, ensuring the UI is rendered and the ref is available, for performing the focus.
        requestAnimationFrame(() => inputInnerRef.current?.focus())
      }
    }, [autoFocus, attached, inputInnerRef])

    if (!attached) {
      const Input = forceNoFormInput ? SearchInput : FormSearchInput
      return (
        <div ref={detachedInputContainerRef}>
          <Input {...inputProps} />
        </div>
      )
    }

    return (
      <div className={clsx(styles.input, attached && styles.attached)}>
        <SearchInput {...inputProps} />
      </div>
    )
  },
)

function sideToRoundedSide(side: "top" | "bottom"): RoundedSide {
  return side === "top" ? "bottom" : "top"
}

interface ComboBoxActionProps {
  children: ReactNode
}

export function ComboboxAction(props: ComboBoxActionProps) {
  const { children } = props

  const { attached } = useComboBox()

  return (
    <div className={clsx(styles.comboboxAction, attached && styles.attached)}>
      {children}
    </div>
  )
}
